import { FC, useEffect } from "react";
import { Router } from "next/router";

import { pageviewFB } from "~lib";
import { ENABLE_TRACKING_SCRIPTS } from "~features/tracking/config";
import { telemetryDebug } from "~lib/debug";

declare interface DataLayerWindow extends Window {
  fbq: any;
  GA_INITIALIZED?: boolean;
}
declare const window: DataLayerWindow;

const initializedPixels = new Set<string>();

export const useFacebookPixelScript = (pixelId: string) => {
  useEffect(() => {
    if (!pixelId || !ENABLE_TRACKING_SCRIPTS) return;

    // Skip if this pixel was already initialized
    if (initializedPixels.has(pixelId)) {
      return;
    }

    // Initialize pixel base code only once globally
    if (!window.fbq) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function (...args: any[]) {
          n.callMethod ? n.callMethod(...args) : n.queue.push(args);
        };
        if (!(f as any)._fbq) (f as any)._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e) as any;
        t.async = true;
        t.defer = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );
    }

    // Initialize this specific pixel
    window.fbq("set", "autoConfig", false, pixelId);
    window.fbq("init", pixelId);

    // disable implicit PageView since they will be sent to all the pixels.
    window.fbq.disablePushState = true;

    initializedPixels.add(pixelId);

    telemetryDebug(`[Meta Pixel] init pixel ID`, pixelId);

    pageviewFB(pixelId);
  }, [pixelId]);
};

interface FacebookPixelScriptProps {
  pixelId?: string;
  router?: Router;
}

export const FacebookPixelScript: FC<FacebookPixelScriptProps> = ({
  pixelId,
}) => {
  useFacebookPixelScript(pixelId);
  return null;
};

export const FacebookPixelRouteTracker: FC<FacebookPixelScriptProps> = ({
  pixelId,
  router,
}) => {
  useEffect(() => {
    if (!router || !pixelId || !window.fbq) return;

    const handleRouteChange = () => {
      if (ENABLE_TRACKING_SCRIPTS) {
        pageviewFB(pixelId);
        telemetryDebug(`[Meta Pixel] route change`, pixelId);
      }
    };

    telemetryDebug(`[Meta Pixel] initiliased route change`, pixelId);

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router?.events, pixelId, router]);

  return null;
};
